import React, { useEffect, useRef, useState } from 'react';

/**
 * App-wide connectivity banner (enterprise-ui plan, Phase 2 item 3).
 *
 * Offline: persistent warning strip. Back online: brief confirmation that
 * auto-dismisses. Pure navigator.onLine + events — no polling, no deps.
 * Mounted once in Layout; fixed to the top, above content but below modals.
 */
const OfflineBanner: React.FC = () => {
  const [offline, setOffline] = useState<boolean>(!navigator.onLine);
  const [showRestored, setShowRestored] = useState(false);
  const restoredTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const onOffline = () => {
      clearTimeout(restoredTimer.current);
      setShowRestored(false);
      setOffline(true);
    };
    const onOnline = () => {
      setOffline(false);
      setShowRestored(true);
      restoredTimer.current = setTimeout(() => setShowRestored(false), 3000);
    };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      clearTimeout(restoredTimer.current);
    };
  }, []);

  if (!offline && !showRestored) return null;

  return (
    <div
      role="status"
      aria-live="assertive"
      data-testid="offline-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '8px 16px',
        paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
        fontSize: 13,
        fontWeight: 600,
        backgroundColor: offline ? 'var(--status-warn-bg)' : 'var(--status-ok-bg)',
        color: offline ? 'var(--status-warn-text)' : 'var(--status-ok-text)',
        borderBottom: `1px solid ${offline ? 'var(--status-warn-border)' : 'var(--status-ok-border)'}`,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: offline ? 'var(--status-warn)' : 'var(--status-ok)',
        }}
      />
      {offline
        ? 'No internet connection — changes can’t be saved until you’re back online'
        : 'Back online'}
    </div>
  );
};

export default OfflineBanner;
